import { Component, ChangeDetectionStrategy, Output, EventEmitter, Input } from '@angular/core';
import { Analyst, IAssetItem } from '@analysis/domain';
import { SampleAiAnalysisStatus } from '@analysis/use-cases';
import { TRulerOption } from '@telespot/shared/viewers/data-access';
import { PresetFilters } from '../../stores/micro-layout/micro-layout.store';
import { IAnalysisState } from '@analysis/domain';
import { ETabContext, ImageFilter } from '../../types/viewer.types';
import { DownloadService } from '@telespot/shared/util';

@Component({
  selector: 'ts-sample-analysis-tools',
  templateUrl: './sample-analysis-tools.component.html',
  styleUrls: ['./sample-analysis-tools.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SampleAnalysisToolsComponent {
  @Input() public readonly syncing: boolean;
  @Input() public readonly busy = false;
  @Input() public readonly mosaicMode: boolean;
  @Input() public readonly rulerActive: boolean;
  @Input() public readonly hasUnsavedData: boolean;
  @Input() public readonly sectorProcessing: boolean;
  @Input() public readonly aiPollingExhausted = false;
  @Input() public readonly aiAnalysisStatus?: SampleAiAnalysisStatus;
  @Input() public readonly aiAnalysisViewOnly = false;
  @Input() public readonly imageFiltersApplied: boolean;
  @Input() public readonly availableAIanalysis: boolean;
  @Input() public readonly downloadAvailable: boolean;
  @Input() public readonly mosaicButtonDisabled: boolean;
  @Input() public readonly isAnalystCurrentUser: boolean;
  @Input() public readonly hspan: number;
  @Input() public readonly numAssets: number;
  @Input() public readonly selectedIndex: number;
  @Input() public readonly selectedPreset: number;
  @Input() public readonly selectedAsset: IAssetItem;
  @Input() public readonly aiAlgorithms: Analyst[];
  @Input() public readonly analystStates: Map<string, IAnalysisState>;
  @Input() public readonly userAnalysts: Analyst[];
  @Input() public readonly analystSelected: Analyst;
  @Input() public readonly imageFilters: ImageFilter[];
  @Input() public readonly panelContext: ETabContext;
  @Input() public readonly rulerIcon: TRulerOption;
  @Input() public readonly rulerSize: number;
  @Input() public readonly layout: 'mobile' | 'web';
  @Input() public readonly editingEnabled: boolean;
  @Input() public readonly isReviewingOtherAnalyst: boolean;
  @Input() public readonly canEdit: boolean;
  @Input() public readonly analystInBreadcrumb = false;

  @Output() exit = new EventEmitter<void>();
  @Output() toggleEditing = new EventEmitter<boolean>();
  @Output() save = new EventEmitter<void>();
  @Output() acceptCompleteAnalysis = new EventEmitter<void>();
  @Output() revertAllChanges = new EventEmitter<void>();
  @Output() downloadAll = new EventEmitter<void>();
  @Output() downloadAsset = new EventEmitter<void>();
  @Output() resetFilters = new EventEmitter<void>();
  @Output() resetRuler = new EventEmitter<void>();
  @Output() toogleReference = new EventEmitter<void>();
  @Output() changeRulerSize = new EventEmitter<number>();
  @Output() toggleMode = new EventEmitter<string>();
  @Output() tooglePanel = new EventEmitter<ETabContext>();
  @Output() updateFilter = new EventEmitter<ImageFilter>();
  @Output() updateFilters = new EventEmitter<PresetFilters>();
  @Output() analystChange = new EventEmitter<Analyst>();
  @Output() changeRulerIcon = new EventEmitter<TRulerOption>();

  public TabContext = ETabContext;

  /**
   * Whether the expansion panel below the toolbar should be open. In breadcrumb-analyst mode
   * the analyst/AI panels no longer exist, so a lingering AI/ANALYST tab context — e.g. carried
   * over from a mosaic↔micro navigation — must not re-open an (empty) panel.
   */
  public get isActionPanelOpen(): boolean {
    if (this.panelContext === undefined) return false;
    if (
      this.analystInBreadcrumb &&
      (this.panelContext === ETabContext.AI || this.panelContext === ETabContext.ANALYST)
    ) {
      return false;
    }
    return true;
  }

  constructor(private readonly downloadService: DownloadService) {}

  public getDownloadFileName(asset: IAssetItem, index: number) {
    const assetIndex = (index + 1).toString();
    const MAX_ZEROS_PAD_START = 4;
    const hspanPrefix = asset?.data?.hSpan ? `hspan_${asset?.data?.hSpan}_` : '';

    return `${assetIndex.padStart(MAX_ZEROS_PAD_START, '0')}_img_${hspanPrefix}${asset.assetFile}`;
  }

  public togglePanel(panel: ETabContext) {
    this.tooglePanel.emit(this.panelContext === panel ? undefined : panel);
  }

  public onDownloadAssetClick(event: Event): void {
    if (!this.downloadAvailable) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    this.downloadAsset.emit();
  }

  public async downloadSelectedAsset(): Promise<void> {
    if (!this.downloadAvailable || !this.selectedAsset?.url) return;
    // Let the host surface its "wait until download completes" feedback.
    this.downloadAsset.emit();
    try {
      await this.downloadService.downloadFile(
        this.selectedAsset.url,
        this.getDownloadFileName(this.selectedAsset, this.selectedIndex)
      );
    } catch {
      // Non-fatal: the host already shows generic feedback.
    }
  }

  public onDownloadAllClick(event: Event): void {
    if (!this.downloadAvailable) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    this.downloadAll.emit();
  }
}
